import { Box, Typography } from '@mui/material';
import { useCallback } from 'react';

import { ErrorAlert, InfoAlert } from '@/components';

import type { FormRow, InputKey } from '../types';
import { getFormMessages, isFormDirty } from '../utils';
import { InviteMembersFormRow } from './InviteMembersFormRow';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from './Table';

interface InviteMembersFormProps {
  editableEmails?: boolean;
  form: FormRow[];
  setForm: React.Dispatch<React.SetStateAction<FormRow[]>>;
  includeRowColumn?: boolean;
  includeWarnings?: boolean;
}

export const InviteMembersForm = ({
  editableEmails = true,
  form,
  setForm,
  includeRowColumn,
  includeWarnings,
}: InviteMembersFormProps) => {
  const isDirty = isFormDirty(form);
  const showRowNumber = includeRowColumn || form.length > 4;
  const formMessages = getFormMessages(form);
  const hasMessages =
    formMessages.errors.length > 0 || formMessages.warnings.length > 0;

  const onChange = useCallback(
    (id: string, key: InputKey, newValue: string) => {
      setForm(rows =>
        rows.map(row =>
          row.id === id
            ? {
                ...row,
                inputs: { ...row.inputs, [key]: newValue },
                errors: { ...row.errors, [key]: false },
              }
            : row,
        ),
      );
    },
    [setForm],
  );

  return (
    <Box sx={styles.root}>
      {isDirty && hasMessages && (
        <Box sx={styles.alerts}>
          {formMessages.errors.map(message => (
            <ErrorAlert key={message} error={message} />
          ))}
          {includeWarnings &&
            formMessages.warnings.map(message => (
              <InfoAlert key={message} message={message} />
            ))}
        </Box>
      )}
      <TableContainer sx={{ maxHeight: 300 }}>
        <Table size="small" aria-label="Add Members">
          <TableHead>
            <TableRow>
              {showRowNumber && (
                <TableCell>
                  <Typography
                    variant="body3SemiBold"
                    sx={{ ...styles.headerLabel, whiteSpace: 'nowrap' }}>
                    Row
                  </Typography>
                </TableCell>
              )}
              <TableCell>
                <Typography variant="body3SemiBold" sx={styles.headerLabel}>
                  Email
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body3SemiBold" sx={styles.headerLabel}>
                  Name
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {form.map((row, index) => (
              <InviteMembersFormRow
                key={row.id}
                id={row.id}
                email={row.inputs.email}
                editableEmails={editableEmails}
                rowNumber={
                  showRowNumber ? (row.originRowNumber ?? index + 1) : undefined
                }
                emailError={row.errors.email}
                name={row.inputs.name}
                nameError={row.errors.name}
                onChange={onChange}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  headerLabel: { textTransform: 'uppercase' },

  alerts: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
};
